import React, { useEffect, useState, useCallback } from 'react';
import {
  Upload,
  Loader2,
  Trash2,
  Eye,
  Mic,
  FileText,
  AlertTriangle,
  Lightbulb,
  CheckCircle2,
  Users2,
  ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const RAG_STYLES = {
  green: { backgroundColor: '#43a047', color: '#fff' },
  amber: { backgroundColor: '#ffb300', color: '#000' },
  red: { backgroundColor: '#ff5252', color: '#fff' },
};

const scoreColor = (s) => {
  const v = Number(s) || 0;
  if (v >= 7.5) return '#43a047';
  if (v >= 5) return '#ffb300';
  return '#ff5252';
};

const DimensionRow = ({ dim }) => (
  <div className="border-b border-ink/5 last:border-0 py-3">
    <div className="flex items-center justify-between gap-3 mb-1">
      <span className="font-bold">{dim.name}</span>
      <span className="font-bold flex-shrink-0">{Math.round((Number(dim.score) || 0) * 10) / 10}/10</span>
    </div>
    <div className="w-full h-2 bg-cream border border-ink/10 rounded-full overflow-hidden mb-1">
      <div
        className="h-full rounded-full"
        style={{ width: `${Math.min(100, (Number(dim.score) || 0) * 10)}%`, backgroundColor: scoreColor(dim.score) }}
      />
    </div>
    {dim.comment && <p className="text-sm font-medium text-ink/60">{dim.comment}</p>}
  </div>
);

const ListCard = ({ title, icon: Icon, items }) => (
  <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-5">
    <h3 className="font-bold tracking-tight text-lg mb-3 flex items-center gap-2">
      <Icon size={18} /> {title}
    </h3>
    <ul className="space-y-2">
      {items.map((s, i) => (
        <li key={i} className="text-sm font-medium text-ink/70 border border-ink/10 rounded-lg bg-cream px-3 py-2">
          {s}
        </li>
      ))}
    </ul>
  </div>
);

const ObservationResult = ({ doc, onDelete, readOnly }) => {
  const a = doc.analysis || {};
  const participation = a.participation || {};
  return (
    <div className="space-y-5">
      <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {doc.source_kind === 'recording' ? <Mic size={18} /> : <FileText size={18} />}
            <span className="font-bold break-all">{doc.filename}</span>
            <span className="text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg bg-cream">
              {a.meeting_type || 'Meeting'}
            </span>
            <span className="text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg" style={RAG_STYLES[a.rag] || RAG_STYLES.amber}>
              {a.rag || 'amber'}
            </span>
          </div>
          {!readOnly && (
            <button
              onClick={() => onDelete(doc.id)}
              className="p-1.5 bg-red-500 text-white border border-ink/10 rounded-lg flex-shrink-0"
              aria-label="Delete observation"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-5 mb-4">
          <div className="bg-cream border border-ink/10 rounded-lg px-5 py-3">
            <div className="text-xs font-bold uppercase tracking-wide text-ink/50">Effectiveness</div>
            <div className="font-bold tracking-tight text-3xl">{Math.round(Number(a.effectiveness_score) || 0)}/100</div>
          </div>
          {a.overview && <p className="font-medium text-ink/70 flex-1 min-w-[200px]">{a.overview}</p>}
        </div>

        {(a.dimensions || []).length > 0 && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-1">Effectiveness dimensions</div>
            {a.dimensions.map((d, i) => (
              <DimensionRow key={i} dim={d} />
            ))}
          </div>
        )}
      </div>

      {(participation.balance_comment || (participation.dominant_voices || []).length > 0 || (participation.quiet_voices || []).length > 0) && (
        <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-5">
          <h3 className="font-bold tracking-tight text-lg mb-3 flex items-center gap-2">
            <Users2 size={18} /> Participation
          </h3>
          {participation.balance_comment && (
            <p className="font-medium text-ink/70 mb-3">{participation.balance_comment}</p>
          )}
          <div className="flex flex-wrap gap-4">
            {(participation.dominant_voices || []).length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-1">Dominant voices</div>
                <div className="flex flex-wrap gap-1">
                  {participation.dominant_voices.map((v, i) => (
                    <span key={i} className="text-xs font-bold px-2 py-0.5 border border-ink/10 rounded-lg" style={{ backgroundColor: '#ffb300' }}>{v}</span>
                  ))}
                </div>
              </div>
            )}
            {(participation.quiet_voices || []).length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-1">Quiet voices</div>
                <div className="flex flex-wrap gap-1">
                  {participation.quiet_voices.map((v, i) => (
                    <span key={i} className="text-xs font-bold px-2 py-0.5 border border-ink/10 rounded-lg bg-cream">{v}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {((a.decisions_made || []).length > 0 || (a.actions_agreed || []).length > 0) && (
        <div className="grid md:grid-cols-2 gap-5">
          {(a.decisions_made || []).length > 0 && (
            <ListCard title="Decisions Made" icon={CheckCircle2} items={a.decisions_made} />
          )}
          {(a.actions_agreed || []).length > 0 && (
            <ListCard title="Actions Agreed" icon={ListChecks} items={a.actions_agreed} />
          )}
        </div>
      )}

      {((a.strengths || []).length > 0 || (a.concerns || []).length > 0 || (a.recommendations || []).length > 0) && (
        <div className="grid md:grid-cols-3 gap-5">
          {(a.strengths || []).length > 0 && (
            <ListCard title="Strengths" icon={CheckCircle2} items={a.strengths} />
          )}
          {(a.concerns || []).length > 0 && (
            <ListCard title="Concerns" icon={AlertTriangle} items={a.concerns} />
          )}
          {(a.recommendations || []).length > 0 && (
            <ListCard title="Recommendations" icon={Lightbulb} items={a.recommendations} />
          )}
        </div>
      )}
    </div>
  );
};

const ObserveTab = ({ reviewId, readOnly }) => {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.listObservations(reviewId);
      setDocs(data.documents || []);
    } catch (e) {
      setDocs([]);
      toast.error('Could not load meeting observations');
    }
  }, [reviewId]);

  useEffect(() => {
    if (reviewId) load();
  }, [reviewId, load]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await api.uploadObservation(reviewId, file);
      toast.success('Meeting analysed');
      await load();
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteObservation(id);
      toast.success('Observation deleted');
      await load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-bold tracking-tight text-2xl mb-1 flex items-center gap-2">
              <Eye size={22} /> Meeting Observation
            </h2>
            <p className="font-medium text-ink/70 max-w-2xl">
              Upload a recording (MP3, WAV, M4A, MP4, WEBM, OGG — max 25MB) or a transcript
              (PDF, DOCX, TXT, VTT, SRT) of a board or committee meeting. The tool assesses how
              effectively the meeting was run — chairing, participation balance, quality of
              debate, decision-making and follow-through.
            </p>
          </div>
          {!readOnly && (
            <label className="inline-flex items-center px-6 py-3 bg-lime border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide cursor-pointer">
              {uploading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
              {uploading ? 'Analysing...' : 'Upload recording / transcript'}
              <input
                type="file"
                className="hidden"
                accept=".mp3,.wav,.m4a,.mp4,.webm,.ogg,.pdf,.docx,.txt,.vtt,.srt"
                disabled={uploading}
                onChange={(e) => {
                  handleUpload(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>
        {uploading && (
          <p className="mt-3 text-sm font-bold uppercase tracking-wide text-ink/50">
            Transcribing and analysing — recordings can take a few minutes...
          </p>
        )}
      </div>

      {docs === null && (
        <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-8 text-center">
          <Loader2 className="animate-spin mx-auto" size={24} />
        </div>
      )}

      {docs && docs.length === 0 && (
        <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-8 text-center">
          <Eye size={36} className="mx-auto mb-3" />
          <h3 className="font-bold tracking-tight text-xl mb-2">No meetings observed yet</h3>
          <p className="font-medium text-ink/70 max-w-md mx-auto">
            Upload a meeting recording or transcript to see an effectiveness assessment here.
          </p>
        </div>
      )}

      {(docs || []).map((doc) => (
        <ObservationResult key={doc.id} doc={doc} onDelete={handleDelete} readOnly={readOnly} />
      ))}
    </div>
  );
};

export default ObserveTab;
